import React, { useEffect, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Box, Typography, Button, Paper, Container, CircularProgress } from '@mui/material';
import { Home, Support, AdminPanelSettings, SupervisedUserCircle } from '@mui/icons-material';
import axios from 'axios';

// Получаем базовый URL API
const getApiUrl = () => {
  if (typeof window !== 'undefined' && window.location) {
    const parts = window.location.pathname.split('/').filter(Boolean);
    if (parts.length >= 2 && parts[0] === 'p') {
      const slug = parts[1];
      return `${window.location.origin}/api/p/${slug}`;
    }
    return `${window.location.origin}/api`;
  }
  return '/api';
};

function NotFoundPage() {
  const { slug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  const authState = useSelector((state) => state.authSlice);
  const role = authState?.role || null;
  
  // Извлекаем slug из URL, если он не в useParams
  const urlMatch = location.pathname.match(/^\/p\/([^/]+)/);
  const slugFromUrl = urlMatch ? urlMatch[1] : null;
  const projectSlug = authState?.projectSlug || slug || slugFromUrl || null;
  const isAuthenticated = authState?.result || false;

  const [errorType, setErrorType] = useState('ROUTE_NOT_FOUND');
  const [isChecking, setIsChecking] = useState(false);

  useEffect(() => {
    // Проверяем, это ошибка проекта?
    // Если URL начинается с /p/:slug и slug есть
    if (slug && location.pathname.startsWith('/p/')) {
      setIsChecking(true);
      // Проверяем через API, существует ли проект
      // Пытаемся получить любую информацию о проекте
      const apiUrl = getApiUrl();
      axios
        .get(`${apiUrl}/data`, {
          validateStatus: (status) => status < 500, // Не бросать ошибку на 4xx
        })
        .then((response) => {
          if (response.status === 404 || response.status === 400) {
            // Проверяем, это ошибка проекта?
            const errorData = response.data;
            if (
              errorData?.error === 'PROJECT_NOT_FOUND' ||
              errorData?.message?.includes('not found in catalog') ||
              errorData?.message?.includes('Project') && errorData?.message?.includes('not found')
            ) {
              setErrorType('PROJECT_NOT_FOUND');
            } else {
              setErrorType('ROUTE_NOT_FOUND');
            }
          } else {
            // Если проект существует, но роут не найден
            setErrorType('ROUTE_NOT_FOUND');
          }
        })
        .catch(() => {
          // При ошибке сети или другой ошибке считаем, что это роут не найден
          setErrorType('ROUTE_NOT_FOUND');
        })
        .finally(() => {
          setIsChecking(false);
        });
    } else {
      // Если это не проектный роут, то это просто несуществующий роут
      setErrorType('ROUTE_NOT_FOUND');
      setIsChecking(false);
    }
  }, [slug, location]);

  const getSuggestions = () => {
    if (errorType === 'PROJECT_NOT_FOUND') {
      const suggestions = [
        {
          text: 'Обратитесь к поставщику услуг для подтверждения существования проекта',
          icon: <Support />,
          action: () => {
            // Можно открыть форму обратной связи или просто показать сообщение
            alert('Обратитесь к вашему поставщику услуг для подтверждения существования проекта.');
          },
        },
      ];

      // Если пользователь авторизован и имеет свой projectSlug, предлагаем вернуться к его проекту
      if (isAuthenticated && projectSlug && projectSlug !== slug) {
        if (role === 'user') {
          suggestions.push({
            text: 'Вернуться к моему проекту',
            icon: <Home />,
            href: `/p/${projectSlug}/groups`,
          });
        } else if (role === 'admin') {
          suggestions.push({
            text: 'Вернуться к моему проекту',
            icon: <AdminPanelSettings />,
            href: `/p/${projectSlug}/admin`,
          });
        }
      } else if (isAuthenticated && (role === 'support' || role === 'superadmin')) {
        // Для саппорта и суперадмина предлагаем их панели
        if (role === 'support') {
          suggestions.push({
            text: 'Перейти в панель саппорта',
            icon: <Support />,
            href: '/v2/support',
          });
        } else {
          suggestions.push({
            text: 'Перейти в панель суперадмина',
            icon: <SupervisedUserCircle />,
            href: '/v2/superadmin',
          });
        }
      }

      return {
        title: 'Проект не найден',
        message: `Проект "${slug || 'unknown'}" не существует в системе.`,
        icon: <Support sx={{ fontSize: 64, color: 'warning.main', mb: 2 }} />,
        suggestions,
      };
    }

    // ROUTE_NOT_FOUND - определяем предложения в зависимости от роли и контекста
    const suggestions = [];
    const isProjectContext = location.pathname.startsWith('/p/');
    const currentProjectSlug = slug || projectSlug;

    if (isAuthenticated) {
      // Авторизованный пользователь
      if (role === 'user' && currentProjectSlug) {
        suggestions.push({
          text: 'Перейти на главную проекта',
          icon: <Home />,
          href: `/p/${currentProjectSlug}/groups`,
        });
      } else if (role === 'admin' && currentProjectSlug) {
        suggestions.push({
          text: 'Перейти в панель администратора',
          icon: <AdminPanelSettings />,
          href: `/p/${currentProjectSlug}/admin`,
        });
      } else if (role === 'support') {
        suggestions.push({
          text: 'Перейти в панель саппорта',
          icon: <Support />,
          href: '/v2/support',
        });
      } else if (role === 'superadmin') {
        suggestions.push({
          text: 'Перейти в панель суперадмина',
          icon: <SupervisedUserCircle />,
          href: '/v2/superadmin',
        });
      } else if (currentProjectSlug) {
        // Если есть projectSlug, но роль не определена, предлагаем главную проекта
        suggestions.push({
          text: 'Перейти на главную проекта',
          icon: <Home />,
          href: `/p/${currentProjectSlug}/groups`,
        });
      }
    } else {
      // Неавторизованный пользователь
      // ВАЖНО: Если на проектной странице, ВСЕГДА предлагаем логин этого проекта
      if (isProjectContext) {
        const projectSlugForLogin = slug || currentProjectSlug;
        if (projectSlugForLogin) {
          suggestions.push({
            text: 'Войти в проект',
            icon: <Home />,
            href: `/p/${projectSlugForLogin}/login`,
          });
        } else {
          // Если slug не определен, все равно пытаемся извлечь из URL
          const urlMatch = location.pathname.match(/^\/p\/([^/]+)/);
          if (urlMatch && urlMatch[1]) {
            suggestions.push({
              text: 'Войти в проект',
              icon: <Home />,
              href: `/p/${urlMatch[1]}/login`,
            });
          }
        }
      } else {
        // Если не на проектной странице, предлагаем базовую главную
        suggestions.push({
          text: 'Перейти на главную',
          icon: <Home />,
          href: '/',
        });
      }
    }

    // Если все еще нет предложений (не проектный контекст и не авторизован)
    // Только тогда предлагаем базовую главную
    if (suggestions.length === 0 && !isProjectContext) {
      suggestions.push({
        text: 'Перейти на главную',
        icon: <Home />,
        href: '/',
      });
    }

    return {
      title: 'Страница не найдена',
      message: 'Запрашиваемая страница не существует.',
      icon: <Home sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />,
      suggestions,
    };
  };

  const content = getSuggestions();

  if (isChecking) {
    return (
      <Container maxWidth="md">
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh',
            py: 4,
          }}
        >
          <Paper
            elevation={3}
            sx={{
              p: 4,
              textAlign: 'center',
              width: '100%',
              maxWidth: 600,
            }}
          >
            <CircularProgress sx={{ mb: 2 }} />
            <Typography variant="body1" color="text.secondary">
              Проверка...
            </Typography>
          </Paper>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          py: 4,
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            textAlign: 'center',
            width: '100%',
            maxWidth: 600,
          }}
        >
          {content.icon}
          <Typography variant="h3" gutterBottom sx={{ fontWeight: 'bold' }}>
            404
          </Typography>
          <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
            {content.title}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            {content.message}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {content.suggestions.map((suggestion, idx) => (
              <Button
                key={idx}
                variant="contained"
                size="large"
                startIcon={suggestion.icon}
                onClick={() => {
                  if (suggestion.href) {
                    navigate(suggestion.href);
                  } else if (suggestion.action) {
                    suggestion.action();
                  }
                }}
                sx={{ width: '100%' }}
              >
                {suggestion.text}
              </Button>
            ))}
          </Box>
        </Paper>
      </Box>
    </Container>
  );
}

export default NotFoundPage;

